import { useState, useRef, useCallback } from 'react'
import { pumpStream } from '@/utils/sse'
import type { SSEEvent } from '@/utils/sse'
import { useAuthStore } from '@/state/authStore'

export interface LangGraphStreamOptions {
  onEvent: (type: string, data: string) => void
  onDone: () => void
  onError: (message: string) => void
}

export function useLangGraphStream(options: LangGraphStreamOptions) {
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  // Sync latest callbacks into refs each render so the stable `stream` function
  // always calls the current version without needing callers to memoize.
  const onEventRef = useRef(options.onEvent)
  const onDoneRef = useRef(options.onDone)
  const onErrorRef = useRef(options.onError)
  onEventRef.current = options.onEvent
  onDoneRef.current = options.onDone
  onErrorRef.current = options.onError

  const stream = useCallback(
    async (endpoint: string, body: Record<string, unknown>) => {
      const controller = new AbortController()
      abortRef.current = controller
      setIsStreaming(true)

      try {
        const token = await useAuthStore.getState().getToken()
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        })

        if (!res.ok || !res.body) {
          onErrorRef.current(`HTTP ${res.status}`)
          return
        }

        const reader = res.body.getReader()
        await pumpStream(reader, (e: SSEEvent) => {
          onEventRef.current(e.event ?? 'data', e.data)
        })
        onDoneRef.current()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        onErrorRef.current(err instanceof Error ? err.message : String(err))
      } finally {
        setIsStreaming(false)
        abortRef.current = null
      }
    },
    [],
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setIsStreaming(false)
  }, [])

  return { stream, isStreaming, reset }
}
